import { groupBy } from 'lodash';
import {
  IEdge,
  IGraph,
  ILabel,
  INode,
  LabelAlongEdgePlacements,
  EdgeLabelPreferredPlacement,
} from '@ardoq/yfiles';
import { PARENT_CHILD_REFERENCE } from '@ardoq/global-consts';
import { referenceInterface } from '@ardoq/reference-interface';
import { AggregatedGraphEdge } from './graphItems/AggregatedGraphEdge';
import { ComponentGraphEdge } from './graphItems/ComponentGraphEdge';
import { DescendantCountLabelStyle } from './yFilesExtensions/DescendantCountLabelStyle';
import { GraphEdge } from './graphItems/GraphEdge';
import {
  isAggregatedGraphEdge,
  isComponentGraphEdge,
  isParentChildGraphEdge,
} from './graphItems/predicates';
import type { YGraphState } from './yFilesExtensions/types';
import { GraphItem } from './graphItems/GraphItem';
import { getUrlFields } from './yFilesExtensions/getUrlFields';
import { GraphItemElementType } from './yFilesExtensions/types';

type GetGraphEdgeKeyArgs = {
  tag: GraphEdge | AggregatedGraphEdge | ComponentGraphEdge;
  sourceGroup: boolean;
  directionId: string;
  relevantNodeId: string;
};
const getGraphEdgeKey = ({
  tag,
  sourceGroup,
  directionId,
  relevantNodeId,
}: GetGraphEdgeKeyArgs) => {
  const isPureComponentGraphEdge = isComponentGraphEdge(tag);
  const modelId = isAggregatedGraphEdge(tag) ? tag.modelIds[0] : tag.modelId;

  const relevantMarker = sourceGroup
    ? tag.getLineBeginning()
    : tag.getLineEnding();

  const urlFieldValues = isPureComponentGraphEdge
    ? tag.getUrlFieldValues()
    : getUrlFields(modelId, GraphItemElementType.EDGE);

  const urlFields =
    urlFieldValues?.map(({ url, label }) => `${label}:${url}`).join(',') ?? '';

  const mainLabel = isPureComponentGraphEdge
    ? (tag.getItemLabels().mainLabel ?? '')
    : referenceInterface.getDisplayLabel(modelId);

  const globalTypeId = isPureComponentGraphEdge
    ? tag.getGlobalTypeId()
    : referenceInterface.getGlobalTypeId(modelId);

  const otherLabels = tag.getItemLabels()?.otherLabels?.join(',') ?? '';
  return `${globalTypeId}${directionId}${relevantNodeId}${relevantMarker}${mainLabel}${urlFields}${otherLabels}`;
};

const nodeId = (node: INode) =>
  (node.tag as GraphItem).id || node.tag.elementId;
const busId = (sourceGroup: boolean) => {
  const directionId = sourceGroup ? '~~from' : '~~to';
  return (edge: IEdge) => {
    if (edge.isSelfLoop) {
      // self-references cannot be grouped with other edges.
      return null;
    }
    const relevantNodeId = sourceGroup
      ? edge.sourceNode === null
        ? null
        : nodeId(edge.sourceNode)
      : edge.targetNode === null
        ? null
        : nodeId(edge.targetNode);
    if (relevantNodeId === null) {
      return null;
    }
    const tag = edge.tag;

    if (isParentChildGraphEdge(tag)) {
      return `${PARENT_CHILD_REFERENCE}${directionId}${relevantNodeId}`;
    }

    const isReferenceEdge =
      'isReference' in tag
        ? tag.isReference()
        : referenceInterface.isReference(tag.modelId ?? '');
    const isAggregated = isAggregatedGraphEdge(tag);

    if (isReferenceEdge || isAggregated) {
      return getGraphEdgeKey({ tag, sourceGroup, directionId, relevantNodeId });
    }

    if ('referenceTypeName' in tag) {
      // 'referenceTypeName' property is only present on Simple Block Diagram edges
      return `${tag.modelId}${tag.referenceTypeName}${directionId}${relevantNodeId}${tag.text}`;
    }

    return null;
  };
};

const edgeGroupsReducer =
  (getGroupId: (edge: IEdge) => string | null) =>
  (edgeGroups: Map<string, IEdge[]>, edge: IEdge) => {
    const groupId = getGroupId(edge);
    if (!groupId) {
      return edgeGroups;
    }
    if (edgeGroups.has(groupId)) {
      edgeGroups.get(groupId)?.push(edge);
    } else {
      edgeGroups.set(groupId, [edge]);
    }

    return edgeGroups;
  };
const invertMapOfArrays = <TKey, TItem>(
  map: Map<TKey, Array<TItem>>
): Map<TItem, TKey> =>
  Array.from(map).reduce((result: Map<TItem, TKey>, [key, items]) => {
    items.forEach(item => result.set(item, key));
    return result;
  }, new Map());

const getSourceGroupId = busId(true);
const getTargetGroupId = busId(false);

const groupIds = (
  graph: IGraph,
  getGroupId: (edge: IEdge) => string | null
) => {
  const edgeGroups = graph.edges.reduce(
    edgeGroupsReducer(getGroupId),
    new Map()
  );

  const edgeGroupKeys = invertMapOfArrays(edgeGroups);
  return {
    edgeGroupKeys,
    edgeGroups,
    groupId: (edge: IEdge) => edgeGroupKeys.get(edge) ?? null,
  };
};
const sourceGrouping = (graph: IGraph) => groupIds(graph, getSourceGroupId);

const targetGrouping = (graph: IGraph) => groupIds(graph, getTargetGroupId);

export const groupEdges = (graph: IGraph) => {
  const {
    edgeGroups: edgeSourceGroups,
    groupId: edgeSourceGroup,
    edgeGroupKeys: edgeSourceGroupKeys,
  } = sourceGrouping(graph);
  const {
    edgeGroups: edgeTargetGroups,
    groupId: edgeTargetGroup,
    edgeGroupKeys: edgeTargetGroupKeys,
  } = targetGrouping(graph);

  // #region ensure each edge appears in _either_ a source group _or_ a target group, but not both.
  for (const edge of graph.edges) {
    const sourceGroupId = edgeSourceGroup(edge);
    if (!sourceGroupId) {
      continue;
    }
    const targetGroupId = edgeTargetGroup(edge);
    if (!targetGroupId) {
      continue;
    }
    const sourceGroup = edgeSourceGroups.get(sourceGroupId)!;
    const targetGroup = edgeTargetGroups.get(targetGroupId)!;
    // remove the edge from whichever group is smaller.
    if (targetGroup.length > sourceGroup.length) {
      sourceGroup.splice(sourceGroup.indexOf(edge), 1);
      edgeSourceGroupKeys.delete(edge);
    } else {
      targetGroup.splice(targetGroup.indexOf(edge), 1);
      edgeTargetGroupKeys.delete(edge);
    }
  }
  // #endregion

  return {
    edgeSourceGroups,
    edgeSourceGroup,
    edgeTargetGroups,
    edgeTargetGroup,
  };
};
const AT_SOURCE_PLACEMENT_DESCRIPTOR = new EdgeLabelPreferredPlacement({
  placementAlongEdge: LabelAlongEdgePlacements.AT_SOURCE,
});
const AT_TARGET_PLACEMENT_DESCRIPTOR = new EdgeLabelPreferredPlacement({
  placementAlongEdge: LabelAlongEdgePlacements.AT_TARGET,
});

interface IdentifyMasterLabelReducerState {
  master: ILabel | null;
  rest: ILabel[];
}
/**
 * @param labelGroups Edge groups with duplicate labels.
 */
const getDuplicateLabels = (labelGroups: ILabel[][]) => {
  const masters: ILabel[] = [];
  const duplicates: ILabel[] = [];
  labelGroups.forEach(labels => {
    if (labels.length < 2) {
      return;
    }
    const duplicateLabelGroups = Object.values(groupBy(labels, 'text')).filter(
      ({ length }) => length > 1
    );
    duplicateLabelGroups.forEach(duplicateLabels => {
      const { master, rest } = duplicateLabels.reduce(
        (state: IdentifyMasterLabelReducerState, current) => {
          if (
            state.master === null &&
            !(current.style instanceof DescendantCountLabelStyle)
          ) {
            return { ...state, master: current };
          }
          state.rest.push(current);
          return state;
        },
        { master: null, rest: [] }
      );
      if (master) {
        masters.push(master);
        duplicates.push(...rest);
      }
    });
  });
  return { masters, duplicates };
};

const isGraphState = (tag: unknown): tag is YGraphState =>
  Boolean(tag && typeof tag === 'object' && 'edgeSourceGroup' in tag);

type LabelGroup = ILabel[];
type LabelGroups = [
  sourceLabelGroups: LabelGroup[],
  targetLabelGroups: LabelGroup[],
];

const edgeGroupLabels = (edgeGroups: Iterable<IEdge[]>) => {
  const result: LabelGroup[] = [];
  for (const edgeGroup of edgeGroups) {
    result.push(edgeGroup.flatMap(edge => edge.labels.toArray()));
  }
  return result;
};

const getLabelGroups = (graph: IGraph): LabelGroups => {
  const { tag: graphState } = graph;
  const { edgeSourceGroups, edgeTargetGroups } = isGraphState(graphState)
    ? graphState
    : groupEdges(graph);

  return [
    edgeGroupLabels(edgeSourceGroups.values()),
    edgeGroupLabels(edgeTargetGroups.values()),
  ];
};
/** be advised, this has side effects on the graph-- it will remove some edge labels.  */
export const processDuplicateLabels = (graph: IGraph) => {
  const [sourceGroupLabels, targetGroupLabels] = getLabelGroups(graph);

  const { masters: sourceGroupMasters, duplicates: sourceGroupDuplicates } =
    getDuplicateLabels(sourceGroupLabels);
  const { masters: targetGroupMasters, duplicates: targetGroupDuplicates } =
    getDuplicateLabels(targetGroupLabels);

  new Set([...sourceGroupDuplicates, ...targetGroupDuplicates]).forEach(
    duplicateLabel => graph.remove(duplicateLabel)
  );
  return new Map([
    ...sourceGroupMasters.map<[ILabel, EdgeLabelPreferredPlacement]>(label => [
      label,
      AT_SOURCE_PLACEMENT_DESCRIPTOR,
    ]),
    ...targetGroupMasters.map<[ILabel, EdgeLabelPreferredPlacement]>(label => [
      label,
      AT_TARGET_PLACEMENT_DESCRIPTOR,
    ]),
  ]);
};
